/**
 * Pendo Integration Helpers
 *
 * Provides helper utilities for Pendo integration including authentication context
 * resolution, feature flag gating, and configuration initialization.
 *
 * @see https://support.pendo.io/hc/en-us/articles/360032202751
 */
import * as store from './store';
import * as events from './event-bus';
import * as env from './env';
import { loadPendo } from '../pendo';

const VISITOR_METADATA_FIELDS = ['firstName', 'lastName', 'email'];
const VISITOR_METADATA_NULL_TEXT = 'null';

// =============================================================================
// Authentication Data Extraction
// =============================================================================

/**
 * Extracts a value from authentication data using multiple paths.
 * @private
 */
function extractAuthValue(passport, idm, paths) {
  for (const path of paths) {
    const value = path(passport, idm);
    if (value) {
      return value;
    }
  }
  return null;
}

function getPassportSalesforceId(passport) {
  return passport && passport.userInfo && passport.userInfo.orgInfo && passport.userInfo.orgInfo.salesforceId;
}

/**
 * Extracts visitor ID from authentication data.
 */
function getVisitorId(passport, idm) {
  return extractAuthValue(passport, idm, [
    (p) => p && p.userInfo && p.userInfo.person && p.userInfo.person.id,
    (p, i) => i && i.user && i.user.profileId
  ]);
}

/**
 * Extracts account ID from authentication data.
 * Prioritizes salesforceId, then orgInfo.orgId with domain.id as fallback.
 */
function getAccountId(passport, idm) {
  const salesforceId = store.get('org-salesforce-id');
  return extractAuthValue(passport, idm, [
    (p) => getPassportSalesforceId(p),
    () => salesforceId, // Prefer Salesforce ID from IDM org-brand when available
    (p) => p && p.userInfo && p.userInfo.orgInfo && p.userInfo.orgInfo.orgId,
    (p) => p && p.userInfo && p.userInfo.domain && p.userInfo.domain.id,
    (p, i) => i && i.userContext && i.userContext.OrganizationId
  ]);
}

/**
 * Extracts account name from authentication data.
 * Prioritizes orgInfo.name with domain.name as fallback.
 */
function getAccountName(passport, idm) {
  return extractAuthValue(passport, idm, [
    (p) => p && p.userInfo && p.userInfo.orgInfo && p.userInfo.orgInfo.name,
    (p) => p && p.userInfo && p.userInfo.domain && p.userInfo.domain.name,
    (p, i) => i && i.userContext && i.userContext.OrganizationName
  ]);
}

// =============================================================================
// Internal vs External User Classification
// =============================================================================

/**
 * Extracts visitor email from authentication data.
 */
function getVisitorEmail(passport, idm) {
  return extractAuthValue(passport, idm, [
    (p) => p && p.userInfo && p.userInfo.person && p.userInfo.person.email,
    (p, i) => i && i.user && i.user.email
  ]);
}

/**
 * Extracts visitor first name from authentication data.
 */
function getVisitorFirstName(passport, idm) {
  return extractAuthValue(passport, idm, [
    (p) => p && p.userInfo && p.userInfo.person && p.userInfo.person.name && p.userInfo.person.name.firstName,
    (p, i) => i && i.user && i.user.firstName
  ]);
}

/**
 * Extracts visitor last name from authentication data.
 */
function getVisitorLastName(passport, idm) {
  return extractAuthValue(passport, idm, [
    (p) => p && p.userInfo && p.userInfo.person && p.userInfo.person.name && p.userInfo.person.name.lastName,
    (p, i) => i && i.user && i.user.lastName
  ]);
}

function normalizeBlankToNull(value) {
  if (value == null) {
    return VISITOR_METADATA_NULL_TEXT;
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return VISITOR_METADATA_NULL_TEXT;
  }

  return value;
}

function normalizeVisitorMetadata(visitorData) {
  if (!visitorData || typeof visitorData !== 'object') {
    return visitorData;
  }

  const normalizedVisitor = Object.assign({}, visitorData);

  VISITOR_METADATA_FIELDS.forEach((field) => {
    normalizedVisitor[field] = normalizeBlankToNull(normalizedVisitor[field]);
  });

  return normalizedVisitor;
}

function resolveUserTypeFromEmail(email, internalDomains) {
  if (!email || typeof email !== 'string') {
    return null;
  }

  const atIndex = email.lastIndexOf('@');
  if (atIndex < 0 || atIndex === email.length - 1) {
    return null;
  }

  const emailDomain = email.slice(atIndex + 1).trim().toLowerCase();
  if (!emailDomain) {
    return null;
  }

  const domainList = Array.isArray(internalDomains) ? internalDomains : [];
  const isInternal = domainList.some((domain) => {
    if (!domain || typeof domain !== 'string') {
      return false;
    }

    const normalizedDomain = domain.trim().toLowerCase().replace(/^@/, '');
    if (!normalizedDomain) {
      return false;
    }

    return emailDomain === normalizedDomain || emailDomain.endsWith(`.${normalizedDomain}`);
  });

  return isInternal ? 'internal' : 'external';
}

function resolveInternalEmailDomains() {
  const idmCfg = store.get('idm');
  let envName = 'prod';

  if (idmCfg && typeof idmCfg.idGatewayUrl === 'string' && idmCfg.idGatewayUrl.trim().length) {
    envName = env.environmentFromIdmGatewayUrl(idmCfg.idGatewayUrl);
  } else if (typeof window !== 'undefined' && window.location && typeof window.location.hostname === 'string') {
    envName = env.environmentFromPassportDomain(window.location.hostname);
  }

  const envValues = env[envName] || env.prod;
  return envValues.pendoInternalEmailDomains;
}

/**
 * Extracts platform organization ID from authentication data.
 * Uses the platform org identifier from the auth payload and does not
 * participate in the existing account ID fallback chain.
 */
function getPlatformOrgId(passport, idm) {
  return extractAuthValue(passport, idm, [
    (p) => p && p.userInfo && p.userInfo.orgInfo && p.userInfo.orgInfo.orgId,
    (p, i) => i && i.userContext && i.userContext.OrganizationId
  ]);
}

/**
 * Resolves visitor and account data from authentication context.
 */
function resolveAuthData(passport, idm) {
  const visitorId = getVisitorId(passport, idm);
  const visitorFirstName = getVisitorFirstName(passport, idm);
  const visitorLastName = getVisitorLastName(passport, idm);
  const visitorEmail = getVisitorEmail(passport, idm);
  const accountId = getAccountId(passport, idm);
  const accountName = getAccountName(passport, idm);
  const platformOrgId = getPlatformOrgId(passport, idm);

  const resolvedDomains = resolveInternalEmailDomains();
  const userType = resolveUserTypeFromEmail(visitorEmail, resolvedDomains);

  const visitor = visitorId ? { id: visitorId } : {};
  visitor.firstName = normalizeBlankToNull(visitorFirstName);
  visitor.lastName = normalizeBlankToNull(visitorLastName);
  visitor.email = normalizeBlankToNull(visitorEmail);
  if (userType) {
    visitor.userType = userType;
  }
  const account = {};
  if (accountId) {
    account.id = accountId;
  }
  if (accountName) {
    account.name = accountName;
  }
  if (platformOrgId) {
    account.platformOrgId = platformOrgId;
  }

  return { visitor: visitor, account: account };
}

// =============================================================================
// Pendo Initialization with Feature Flag Gating
// =============================================================================

/**
 * Merges app-provided data with platform-resolved data.
 * @private
 */
function mergeData(platformData, appData) {
  if (!appData || typeof appData !== 'object') {
    return platformData;
  }

  const merged = {};

  for (const key in platformData) {
    if (platformData.hasOwnProperty(key)) {
      merged[key] = platformData[key];
    }
  }

  for (const key in appData) {
    if (appData.hasOwnProperty(key)) {
      merged[key] = appData[key];
    }
  }

  return merged;
}

function waitForSalesforceIdReady(callback) {
  const passport = store.get('passport-data');
  const idm = store.get('idm-data');
  const passportSalesforceId = getPassportSalesforceId(passport);
  const storeSalesforceId = store.get('org-salesforce-id');

  if (!idm || passportSalesforceId || storeSalesforceId) {
    callback();
    return;
  }

  // Use greedy listener so it fires even if this event already emitted.
  events.once('org-salesforce-id-ready', callback, { greedy: true });
}

/**
 * Starts Pendo integration with feature flag check.
 * Automatically waits for salesforceId to be available before initialization.
 *
 * @param {string} apiKey - Pendo API key
 * @param {Object} [visitorData] - Visitor data with optional 'id' (merged with platform data if 'id' is omitted)
 * @param {Object} [accountData] - Account data with optional 'id' (merged with platform data if 'id' is omitted)
 * @param {number} [idleTimeout=500] - Timeout in milliseconds for requestIdleCallback fallback
 * @param {Object} [options] - Additional Pendo initialization options
 * @returns {Promise<boolean>} Resolves to true if loaded, false otherwise
 *
 * @example
 * sidekick.startPendo('7cec4528-5e4e-49cd-8c75-834c5c6eb839');
 *
 * @example
 * sidekick.startPendo('API-KEY', { id: 'user123', department: 'Engineering' }, { id: 'org456', plan: 'Enterprise' });
 *
 * @example
 * sidekick.startPendo('API-KEY', { department: 'Engineering' }, { plan: 'Enterprise' });
 */
export default function startPendo(apiKey, visitorData, accountData, idleTimeout, options) {
  if (!apiKey || typeof apiKey !== 'string') {
    console.error('[Pendo] API key is required.');
    return Promise.resolve(false);
  }

  if (visitorData && visitorData.id && accountData && accountData.id) {
    const normalizedVisitor = normalizeVisitorMetadata(visitorData);
    loadPendo(apiKey, normalizedVisitor, accountData, idleTimeout, options);
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const handleFeatureFlagCheck = (flagsSvc) => {
      waitForSalesforceIdReady(() => {
        const idm = store.get('idm-data');
        const passport = store.get('passport-data');

        flagsSvc
          .checkState('platform_pendo_enabled')
          .then((enabled) => {
            if (!enabled) {
              console.warn('[Pendo] Feature flag disabled. Not loading.');
              resolve(false);
              return;
            }

            const authData = resolveAuthData(passport, idm);

            let mergedVisitor = (visitorData && !visitorData.id) ? mergeData(authData.visitor, visitorData) : (visitorData || authData.visitor);
            let mergedAccount = (accountData && !accountData.id) ? mergeData(authData.account, accountData) : (accountData || authData.account);

            // Preserve explicit app account IDs, but prioritize Salesforce ID for resolved account IDs.
            if (!(accountData && accountData.id)) {
              const resolvedSalesforceId = extractAuthValue(passport, idm, [
                (p) => getPassportSalesforceId(p),
                () => store.get('org-salesforce-id')
              ]);
              if (resolvedSalesforceId) {
                mergedAccount = Object.assign({}, mergedAccount, { id: resolvedSalesforceId });
              }
            }

            if (authData.visitor && authData.visitor.userType && mergedVisitor && !mergedVisitor.userType) {
              mergedVisitor = Object.assign({}, mergedVisitor, { userType: authData.visitor.userType });
            }

            const normalizedVisitor = normalizeVisitorMetadata(mergedVisitor);

            loadPendo(
              apiKey,
              normalizedVisitor,
              mergedAccount,
              idleTimeout,
              options
            );
            resolve(true);
          })
          .catch((error) => {
            console.error('[Pendo] Feature flag check failed:', error);
            resolve(false);
          });
      });
    };

    events.once('feature-flags-ready-cc', handleFeatureFlagCheck, { greedy: true });
  });
}

// =============================================================================
// Configuration Initialization
// =============================================================================

/**
 * Initializes Pendo from configuration object.
 *
 * @param {Object} cfg - Configuration object from sidekick.init()
 * @param {Function} startPendoFn - The startPendo function to call
 */
export function initializePendoFromConfig(cfg, startPendoFn) {
  if (!cfg || !cfg.pendo || !cfg.pendo.apiKey) {
    return;
  }

  const idleTimeout = cfg.pendo.idleTimeout;
  const visitorData = cfg.pendo.visitor || null;
  let accountData = cfg.pendo.account || null;
  const options = {};

  // Merge moduleName and productId into account data if provided
  if (cfg.moduleName || cfg.productId) {
    const platformMetadata = {};
    if (cfg.moduleName) {
      platformMetadata.moduleName = cfg.moduleName;
    }
    if (cfg.productId) {
      platformMetadata.productId = cfg.productId;
    }
    accountData = accountData ? Object.assign({}, accountData, platformMetadata) : platformMetadata;
  }

  if (cfg.pendo.location) {
    options.location = cfg.pendo.location;
  }

  if (cfg.pendo.events) {
		options.events = cfg.pendo.events;
  }

  startPendoFn(cfg.pendo.apiKey, visitorData, accountData, idleTimeout, options).catch((error) => {
    console.error('[Pendo] Initialization failed:', error);
  });
}
